import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "./GoalButton";
import SuiInput from "components/SuiInput";
import GoalStatusChip from "./GoalStatusChip";
import { GOAL_CATEGORY_LIST, stripNegative } from "../goals.constants";

// "25" not "25.00" - scores/weights come back as fixed-2 decimal strings.
const trimNumber = (value) => Number(Number(value).toFixed(2));

const isBlank = (value) => value === null || value === undefined || value === "";

const dash = (value) => (isBlank(value) ? "—" : value);

// (achieved/allocated) for a category, e.g. "(24/25)". Unmarked goals count as
// nothing yet, so it's "—" until at least one goal in the category is marked.
const categoryScore = (categoryGoals) => {
  const allocated = categoryGoals.reduce((sum, goal) => sum + Number(goal.weight_percentage), 0);
  const marked = categoryGoals.filter((goal) => !isBlank(goal.achieved_percentage));
  const achieved = marked.reduce((sum, goal) => sum + Number(goal.achieved_percentage), 0);
  return `(${marked.length ? trimNumber(achieved) : "—"}/${trimNumber(allocated)})`;
};

// Fixed widths (with table-layout: fixed below) so every period's table lines
// up identically, whatever text is in it.
const GOAL_TABLE_COLUMNS = [
  { label: "Type", width: "18%" },
  { label: "Goals", width: "30%" },
  { label: "Score", width: "16%" },
  { label: "Notes", width: "36%" },
];

// Native-field look shared by the score input and the notes textarea.
const fieldSx = (hasError = false) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  padding: "0 12px",
  fontFamily: "inherit",
  fontSize: 14,
  color: "#495057",
  backgroundColor: "#fff",
  border: `1px solid ${hasError ? "#ea0606" : "#d2d6da"}`,
  borderRadius: "8px",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  "&::placeholder": { color: "#a4adbd" },
  "&:focus": {
    borderColor: hasError ? "#ea0606" : "#17c1e8",
    boxShadow: `0 0 0 3px ${hasError ? "rgba(234, 6, 6, 0.15)" : "rgba(23, 193, 232, 0.2)"}`,
  },
});

const MARKABLE_STATUSES = ["active", "pending_review"];

// One goal period as a card: header, who set/reviewed it, and the goals table.
// The current (unfinished) period is also where review happens - once it's
// active, its Score and Notes cells become inputs, with the save / submit /
// confirm actions underneath. A draft is never markable (setup has to be
// confirmed first), and completed periods are read-only history.
// Amber callout, same tone as the draft/pending status pill so it stays readable.
function GoalNotice({ children }) {
  return (
    <SuiBox
      display="flex"
      alignItems="center"
      gap={1}
      mb={2}
      px={1.5}
      py={1}
      sx={{
        borderRadius: "10px",
        backgroundColor: "#fff4d6",
        borderLeft: "3px solid #f2b705",
        color: "#7a5200",
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      <Icon sx={{ fontSize: "18px !important", color: "#946200" }}>info</Icon>
      <span>{children}</span>
    </SuiBox>
  );
}

GoalNotice.propTypes = { children: PropTypes.node.isRequired };

function GoalPeriodCard({ period, isCurrent, hook, onChanged }) {
  const { markGoalItem, submitReview, confirmReview, rejectReview } = hook;

  const [markings, setMarkings] = useState({});
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectField, setShowRejectField] = useState(false);
  const [isActing, setIsActing] = useState(false);

  const goals = period.goals || [];
  const canMark = isCurrent && MARKABLE_STATUSES.includes(period.status);
  const isCompleted = period.status === "completed";

  useEffect(() => {
    const initial = {};
    goals.forEach((goal) => {
      initial[goal.id] = {
        achieved_percentage: isBlank(goal.achieved_percentage)
          ? ""
          : trimNumber(goal.achieved_percentage),
        review_notes: goal.review_notes ?? "",
      };
    });
    setMarkings(initial);
    setShowRejectField(false);
  }, [period]);

  const updateMarking = (goalId, field, value) =>
    setMarkings({ ...markings, [goalId]: { ...markings[goalId], [field]: value } });

  const runAction = async (action) => {
    setIsActing(true);
    const result = await action();
    setIsActing(false);
    if (result) onChanged();
  };

  // One save for every row at once, rather than a per-row button (which was
  // confusing - it wasn't obvious a row's score/notes hadn't actually been
  // sent to the server until this ran).
  const saveAllMarkings = async () => {
    for (const goal of goals) {
      const marking = markings[goal.id];
      await markGoalItem(goal.id, {
        achieved_percentage:
          marking.achieved_percentage === "" ? undefined : Number(marking.achieved_percentage),
        review_notes: marking.review_notes,
      });
    }
  };

  const handleSaveMarks = async () => {
    setIsActing(true);
    await saveAllMarkings();
    setIsActing(false);
    onChanged();
  };

  const handleSubmitReview = async () => {
    setIsActing(true);
    await saveAllMarkings();
    const result = await submitReview(period.id);
    setIsActing(false);
    if (result) onChanged();
  };

  // Scores must be within 0..weight; anything else is flagged inline and
  // blocks Save / Submit so bad values never reach the server.
  const scoreError = (goal) => {
    const raw = markings[goal.id]?.achieved_percentage;
    if (isBlank(raw)) return "";
    const score = Number(raw);
    if (Number.isNaN(score) || score < 0) return "Enter a valid score";
    if (score > Number(goal.weight_percentage)) {
      return `Max ${trimNumber(goal.weight_percentage)}`;
    }
    return "";
  };
  const hasScoreErrors = goals.some((goal) => scoreError(goal));

  const allMarked = goals.every((goal) => !isBlank(markings[goal.id]?.achieved_percentage));

  // One group per category (in the fixed category order), so a category's
  // name is written once and spans all of its goal rows.
  const goalGroups = GOAL_CATEGORY_LIST.map((category) => ({
    category,
    goals: goals
      .filter((goal) => goal.category === category)
      .sort((a, b) => a.sort_order - b.sort_order),
  })).filter((group) => !isEmpty(group.goals));

  return (
    <Card sx={{ mb: 3 }}>
      <SuiBox p={3}>
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <SuiBox>
            <SuiTypography variant="h6" fontWeight="bold">
              {isCurrent ? "Current Goals" : `${period.template?.label || "Custom period"} Goals`}
            </SuiTypography>
            <SuiTypography variant="caption" color="secondary" display="block">
              {isCurrent && `${period.template?.label || "Custom period"} · `}
              {period.start_date} — {period.end_date}
            </SuiTypography>
          </SuiBox>
          <SuiBox display="flex" alignItems="center" gap={2}>
            {isCompleted && (
              <SuiTypography variant="button" fontWeight="medium">
                Final score: {dash(period.actual_percentage)} / {period.total_percentage}%
              </SuiTypography>
            )}
            <GoalStatusChip status={period.status} />
          </SuiBox>
        </SuiBox>

        <SuiTypography variant="caption" color="text" display="block" mb={2}>
          Set by {dash(period.created_by?.name)}
          {period.setup_confirmed_by && ` · Setup confirmed by ${period.setup_confirmed_by.name}`}
          {period.reviewed_by && ` · Reviewed by ${period.reviewed_by.name}`}
          {period.review_confirmed_by &&
            ` · Review confirmed by ${period.review_confirmed_by.name}`}
        </SuiTypography>

        {isCurrent && period.status === "draft" && (
          <GoalNotice>
            Goals can&apos;t be marked until the setup is saved as active - use Edit Setup above.
          </GoalNotice>
        )}
        {isCurrent && period.status === "pending_review" && (
          <GoalNotice>
            Review submitted by {period.reviewed_by?.name || "—"}. Needs confirmation to complete.
          </GoalNotice>
        )}

        {isEmpty(goals) ? (
          <SuiTypography variant="caption" color="secondary">
            No goals were added to this period.
          </SuiTypography>
        ) : (
          <TableContainer sx={{ boxShadow: "none" }}>
            <MuiTable size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
              {/* The theme sets thead to display:block, which detaches the header from the body's column widths. */}
              <TableHead sx={{ display: "table-header-group", p: 0 }}>
                <TableRow>
                  {GOAL_TABLE_COLUMNS.map(({ label, width }) => (
                    <TableCell key={label} sx={{ width }}>
                      <SuiTypography variant="caption" fontWeight="bold" color="secondary">
                        {label.toUpperCase()}
                      </SuiTypography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {goalGroups.flatMap((group) =>
                  group.goals.map((goal, index) => (
                    <TableRow key={goal.id} sx={{ "& > td": { verticalAlign: "top !important" } }}>
                      {index === 0 && (
                        <TableCell
                          rowSpan={group.goals.length}
                          sx={{ verticalAlign: "top !important" }}
                        >
                          <SuiTypography
                            variant="button"
                            fontWeight="medium"
                            display="block"
                            mt={canMark ? 1.2 : 0}
                          >
                            {group.category}
                          </SuiTypography>
                          <SuiTypography variant="caption" color="secondary">
                            {categoryScore(group.goals)}
                          </SuiTypography>
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        <SuiTypography
                          variant="button"
                          fontWeight="regular"
                          display="block"
                          mt={canMark ? 1.2 : 0}
                        >
                          {goal.description}
                        </SuiTypography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {canMark ? (
                          <SuiBox>
                            <SuiBox sx={{ position: "relative", width: 128, maxWidth: "100%" }}>
                              <SuiBox
                                component="input"
                                type="number"
                                placeholder="Score"
                                min={0}
                                max={goal.weight_percentage}
                                onWheel={(e) => e.target.blur()}
                                value={markings[goal.id]?.achieved_percentage ?? ""}
                                onChange={(e) =>
                                  updateMarking(
                                    goal.id,
                                    "achieved_percentage",
                                    stripNegative(e.target.value)
                                  )
                                }
                                sx={{
                                  ...fieldSx(!!scoreError(goal)),
                                  MozAppearance: "textfield",
                                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  height: 40,
                                  paddingRight: "44px",
                                }}
                              />
                              <SuiTypography
                                variant="caption"
                                fontWeight="medium"
                                color="secondary"
                                sx={{
                                  position: "absolute",
                                  right: 12,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                / {trimNumber(goal.weight_percentage)}
                              </SuiTypography>
                            </SuiBox>
                            {scoreError(goal) && (
                              <SuiTypography
                                variant="caption"
                                display="block"
                                sx={{ color: "#c01616 !important", fontWeight: 500 }}
                              >
                                {scoreError(goal)}
                              </SuiTypography>
                            )}
                          </SuiBox>
                        ) : (
                          <SuiTypography variant="caption">
                            {isBlank(goal.achieved_percentage)
                              ? "—"
                              : trimNumber(goal.achieved_percentage)}{" "}
                            / {trimNumber(goal.weight_percentage)}
                          </SuiTypography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {canMark ? (
                          <SuiBox
                            component="textarea"
                            rows={1}
                            // Start at 80px and grow only when the text needs it.
                            ref={(el) => {
                              if (!el) return;
                              el.style.height = "auto";
                              el.style.height = `${Math.max(80, el.scrollHeight + 2)}px`;
                            }}
                            placeholder="Notes on this goal…"
                            value={markings[goal.id]?.review_notes ?? ""}
                            onChange={(e) => updateMarking(goal.id, "review_notes", e.target.value)}
                            sx={{
                              ...fieldSx(),
                              minHeight: 80,
                              padding: "10px 12px",
                              lineHeight: 1.5,
                              resize: "vertical",
                            }}
                          />
                        ) : (
                          <SuiTypography variant="caption">{dash(goal.review_notes)}</SuiTypography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </MuiTable>
          </TableContainer>
        )}

        {canMark && (
          <SuiBox display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={3}>
            <GoalButton
              variant="tonal"
              icon="save"
              loading={isActing}
              onClick={handleSaveMarks}
              disabled={isActing || hasScoreErrors}
            >
              Save Marks
            </GoalButton>
            {period.status === "active" && (
              <GoalButton
                variant="primary"
                icon="send"
                loading={isActing}
                onClick={handleSubmitReview}
                disabled={isActing || !allMarked || hasScoreErrors}
              >
                Submit for Review
              </GoalButton>
            )}
            {period.status === "pending_review" && (
              <>
                {showRejectField && (
                  <SuiInput
                    placeholder="Reason for sending back"
                    value={rejectNotes}
                    onChange={(e) => setRejectNotes(e.target.value)}
                    sx={{ width: 220 }}
                  />
                )}
                <GoalButton
                  variant="dangerSoft"
                  onClick={() =>
                    showRejectField
                      ? runAction(() => rejectReview(period.id, rejectNotes))
                      : setShowRejectField(true)
                  }
                  loading={isActing}
                >
                  {showRejectField ? "Confirm Reject" : "Reject Review"}
                </GoalButton>
                <GoalButton
                  icon="check_circle"
                  loading={isActing}
                  variant="primary"
                  onClick={() => runAction(() => confirmReview(period.id))}
                  disabled={isActing}
                >
                  Confirm Review
                </GoalButton>
              </>
            )}
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

export default GoalPeriodCard;
